import { useEffect } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import "@/App.css";
import { AuthProvider } from "@/context/AuthContext";
import { SmoothScroll } from "@/components/motion/SmoothScroll";
import { CustomCursor } from "@/components/motion/CustomCursor";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import HomePage from "@/pages/HomePage";
import ExhibitionsPage from "@/pages/ExhibitionsPage";
import ExhibitionDetailPage from "@/pages/ExhibitionDetailPage";
import CVPage from "@/pages/CVPage";
import PressPage from "@/pages/PressPage";
import ContactPage from "@/pages/ContactPage";
import LegalPage from "@/pages/LegalPage";
import NotFoundPage from "@/pages/NotFoundPage";
import AdminLoginPage from "@/pages/admin/LoginPage";
import ForgotPasswordPage from "@/pages/admin/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/admin/ResetPasswordPage";
import { AdminLayout } from "@/pages/admin/AdminLayout";
import DashboardPage from "@/pages/admin/DashboardPage";
import HomeSettingsPage from "@/pages/admin/HomeSettingsPage";
import EditionsPage from "@/pages/admin/EditionsPage";
import ExhibitionsListPage from "@/pages/admin/ExhibitionsListPage";
import ExhibitionEditPage from "@/pages/admin/ExhibitionEditPage";
import RecentShowsPage from "@/pages/admin/RecentShowsPage";
import CVAdminPage from "@/pages/admin/CVAdminPage";
import PressAdminPage from "@/pages/admin/PressAdminPage";
import EnquiriesPage from "@/pages/admin/EnquiriesPage";
import SiteSettingsPage from "@/pages/admin/SiteSettingsPage";
import UsersPage from "@/pages/admin/UsersPage";

const PageShell = ({ children }) => (
  <motion.main
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.4, ease: "easeOut" }}
  >
    {children}
  </motion.main>
);

const RoutedPages = () => {
  const location = useLocation();

  useEffect(() => {
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);

  const isAdmin = location.pathname.startsWith("/admin");

  return (
    <div>
      {!isAdmin && <SmoothScroll />}
      {!isAdmin && <CustomCursor />}
      {!isAdmin && <Header />}
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<PageShell><HomePage /></PageShell>} />
          <Route path="/exhibitions" element={<PageShell><ExhibitionsPage /></PageShell>} />
          <Route path="/exhibitions/:slug" element={<PageShell><ExhibitionDetailPage /></PageShell>} />
          <Route path="/johnny-depp/cv" element={<PageShell><CVPage /></PageShell>} />
          <Route path="/press" element={<PageShell><PressPage /></PageShell>} />
          <Route path="/contact" element={<PageShell><ContactPage /></PageShell>} />
          <Route path="/privacy-policy" element={<PageShell><LegalPage kind="privacy_policy" title="Privacy Policy" /></PageShell>} />
          <Route path="/terms-of-service" element={<PageShell><LegalPage kind="terms_of_service" title="Terms of Service" /></PageShell>} />
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/admin/reset-password" element={<ResetPasswordPage />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<DashboardPage />} />
            <Route path="home" element={<HomeSettingsPage />} />
            <Route path="editions" element={<EditionsPage />} />
            <Route path="exhibitions" element={<ExhibitionsListPage />} />
            <Route path="exhibitions/:id" element={<ExhibitionEditPage />} />
            <Route path="recent-shows" element={<RecentShowsPage />} />
            <Route path="cv" element={<CVAdminPage />} />
            <Route path="press" element={<PressAdminPage />} />
            <Route path="enquiries" element={<EnquiriesPage />} />
            <Route path="settings" element={<SiteSettingsPage />} />
            <Route path="users" element={<UsersPage />} />
          </Route>
          <Route path="*" element={<PageShell><NotFoundPage /></PageShell>} />
        </Routes>
      </AnimatePresence>
      {!isAdmin && <Footer />}
    </div>
  );
};

function App() {
  useEffect(() => {
    const kit = process.env.REACT_APP_FONTS_KIT_URL;
    if (kit) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = kit;
      document.head.appendChild(link);
    }
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <RoutedPages />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
